import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { MusicalWorkViewStore } from "../../store/MusicalWorkViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { ShowItemsCompact } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { GridColumn } from "../../../common/types";
import { FormControl } from "@mui/material";
import { ShowReferenceSet } from "../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { InputHtmlString } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import {
  SharesPieChart,
  computeSharesFromWorks,
} from "../../../../common/components/SharesPieChart";

export type MusicalWorkViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  titleValue?: { displayId: string; id: string } | null;
  title: string;
  musicalGenreValue: { displayId: string; id: string } | null;
  alternativeTitlesValue?: { displayId: string; id: string } | null;
  alternativeTitles: string[];
  authorsRows: CrudRows;
  authorsVisibleColumns: GridColumn[];
  authorsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  notesValue?: { displayId: string; id: string } | null;
  notes: string;
  performersValues: { displayId: string; id: string }[];
  cmsIdValue?: { displayId: string; id: string } | null;
  cmsId: string;
  territoriesValues: { displayId: string; id: string }[];
  iswcValue?: { displayId: string; id: string } | null;
  iswc: string;
  workNumberValue?: { displayId: string; id: string } | null;
  workNumber: number;
  scoreFiles: CrudRows;
  scoreViewAction?: IMenuAction[];
  phonogramsRows: CrudRows;
  phonogramsVisibleColumns: GridColumn[];
  phonogramsSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  originalLyricsValue?: { displayId: string; id: string } | null;
  originalLyrics: string;
  contractObservationValue?: { displayId: string; id: string } | null;
  contractObservation: string;
  createdAt_Value?: { displayId: string; id: string } | null;
  createdAt_: Date | null;
  lastUpdatedAt_Value?: { displayId: string; id: string } | null;
  lastUpdatedAt_: Date | null;
  workWithAuthors?: any[];
  musicalWorkViewStore: MusicalWorkViewStore;
};

export const MusicalWorkViewForm: React.FC<MusicalWorkViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    title = "",
    musicalGenreValue,
    alternativeTitles = [],
    authorsRows,
    authorsVisibleColumns,
    authorsSetColumnWiths,
    notes = "",
    performersValues,
    cmsId = "",
    territoriesValues,
    iswc = "",
    workNumber = 0,
    scoreFiles,
    scoreViewAction,
    phonogramsRows,
    phonogramsVisibleColumns,
    phonogramsSetColumnWiths,
    originalLyrics = "",
    contractObservation = "",
    createdAt_ = null,
    lastUpdatedAt_ = null,
    workWithAuthors = [],
    musicalWorkViewStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="workInfo"
            title={langStore.groupMusicalWorkWorkInfoLabel}
            collapsible={true}
            expanded={musicalWorkViewStore.getCardState("workInfo")}
            toggleExpand={() =>
              musicalWorkViewStore.toggleCardState("workInfo")
            }
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "0 0 400px" }}>
                <FormControl fullWidth>
                  <ShowMultiLine
                    value={title}
                    label={langStore.MusicalWorkTitleLabel}
                  />
                  <ShowReference
                    label={langStore.MusicalWorkMusicalGenreLabel}
                    selectedValue={langStore.displayIdByLang(
                      musicalGenreValue?.displayId
                    )}
                  />
                  <ShowMultiLine
                    value={alternativeTitles}
                    label={langStore.MusicalWorkAlternativeTitlesLabel}
                  />
                  <ShowItemsCompact
                    rows={authorsRows}
                    showLabel={true}
                    label={langStore.MusicalWorkAuthorsLabel}
                    columns={authorsVisibleColumns}
                    setColumnWidths={authorsSetColumnWiths}
                    entityName="WorkAuthor"
                    enableDownload={true}
                    onRowClick={(rowId) =>
                      rootStore.openSubentityModal("workAuthor", rowId)
                    }
                  />
                  <ShowMultiLine
                    value={notes}
                    label={langStore.MusicalWorkNotesLabel}
                  />
                </FormControl>
              </Box>
              {workWithAuthors.length > 0 && (
                <Box sx={{ flex: "1 1 400px", minWidth: 400 }}>
                  <SharesPieChart
                    data={computeSharesFromWorks(workWithAuthors)}
                    title="Distribución Autores y Editoriales"
                    subtitle="Participación neta de autores y editoriales"
                  />
                </Box>
              )}
            </Box>
          </GroupContainer>

          <GroupContainer
            id="workRegistration"
            title={langStore.groupMusicalWorkWorkRegistrationLabel}
            collapsible={true}
            expanded={musicalWorkViewStore.getCardState("workRegistration")}
            toggleExpand={() =>
              musicalWorkViewStore.toggleCardState("workRegistration")
            }
          >
            <FormControl fullWidth>
              <ShowReferenceSet
                label={langStore.MusicalWorkPerformersLabel}
                selectedValues={performersValues}
              />
              <ShowMultiLine
                value={cmsId}
                label={langStore.MusicalWorkCmsIdLabel}
              />
              <ShowReferenceSet
                label={langStore.MusicalWorkTerritoriesLabel}
                selectedValues={territoriesValues}
              />
              <ShowMultiLine
                value={iswc}
                label={langStore.MusicalWorkIswcLabel}
              />
              <ShowNumber
                value={workNumber}
                label={langStore.MusicalWorkWorkNumberLabel}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="workContent"
            title={langStore.groupMusicalWorkWorkContentLabel}
            collapsible={true}
            expanded={musicalWorkViewStore.getCardState("workContent")}
            toggleExpand={() =>
              musicalWorkViewStore.toggleCardState("workContent")
            }
          >
            <FormControl fullWidth>
              <ShowFilesCompact
                rows={scoreFiles}
                showLabel={true}
                label={langStore.MusicalWorkScoreLabel}
                menuActions={scoreViewAction}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
              />
              <ShowItemsCompact
                rows={phonogramsRows}
                showLabel={true}
                label={langStore.MusicalWorkPhonogramsLabel}
                columns={phonogramsVisibleColumns}
                setColumnWidths={phonogramsSetColumnWiths}
                entityName="Phonogram"
                enableDownload={true}
                onRowClick={(rowId) =>
                  rootStore.openSubentityModal("phonogram", rowId)
                }
              />
              <ShowMultiLine
                value={originalLyrics}
                label={langStore.MusicalWorkOriginalLyricsLabel}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="contracts"
            title={langStore.groupMusicalWorkContractsLabel}
            collapsible={true}
            expanded={musicalWorkViewStore.getCardState("contracts")}
            toggleExpand={() =>
              musicalWorkViewStore.toggleCardState("contracts")
            }
          >
            <FormControl fullWidth>
              <InputHtmlString
                value={contractObservation}
                onChange={() => {}}
                label={langStore.MusicalWorkContractObservationLabel}
                readOnly={true}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="systemDates"
            title={langStore.groupMusicalWorkSystemDatesLabel}
            collapsible={true}
            expanded={musicalWorkViewStore.getCardState("systemDates")}
            toggleExpand={() =>
              musicalWorkViewStore.toggleCardState("systemDates")
            }
          >
            <FormControl fullWidth>
              <ShowDateTime
                value={createdAt_}
                label={langStore.MusicalWorkCreatedAt_Label}
              />
              <ShowDateTime
                value={lastUpdatedAt_}
                label={langStore.MusicalWorkLastUpdatedAt_Label}
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
